import React, { useState, useRef, useEffect } from "react";
import { Send, Bot, User, Headphones, Megaphone, Network } from "lucide-react";
import { API } from "@/lib/api";
import { useLocale } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const AGENTS = [
  { id: "customer_service", label: "Atendimento", icon: Headphones },
  { id: "sales", label: "Vendas", icon: Megaphone },
  { id: "orchestrator", label: "Orquestrador", icon: Network },
];

export default function ChatPanel() {
  const { locale } = useLocale();
  const [agent, setAgent] = useState("customer_service");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const sessionRef = useRef(null);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const switchAgent = (id) => { setAgent(id); setMessages([]); sessionRef.current = null; };

  const send = async (e) => {
    e.preventDefault();
    const text = input.trim();
    if (!text || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setStreaming(true);
    try {
      const resp = await fetch(`${API}/ai/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ message: text, agent, session_id: sessionRef.current, locale }),
      });
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const p of parts) {
          const line = p.trim();
          if (!line.startsWith("data:")) continue;
          try {
            const evt = JSON.parse(line.slice(5).trim());
            if (evt.delta) {
              setMessages((m) => { const copy = [...m]; copy[copy.length - 1].content += evt.delta; return copy; });
            } else if (evt.session_id) {
              sessionRef.current = evt.session_id;
            } else if (evt.error) {
              setMessages((m) => { const copy = [...m]; copy[copy.length - 1].content = "Erro: " + evt.error; return copy; });
            }
          } catch {}
        }
      }
    } catch (err) {
      setMessages((m) => { const copy = [...m]; copy[copy.length - 1].content = "Falha na conexão."; return copy; });
    } finally {
      setStreaming(false);
    }
  };

  return (
    <div className="animate-float-up flex flex-col h-[calc(100vh-9rem)]">
      <div className="flex gap-2 mb-4">
        {AGENTS.map((a) => (
          <button key={a.id} onClick={() => switchAgent(a.id)} data-testid={`chat-agent-${a.id}`}
            className={`flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium border transition-colors ${agent === a.id ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:bg-secondary"}`}>
            <a.icon className="h-4 w-4" /> {a.label}
          </button>
        ))}
      </div>

      <div className="flex-1 rounded-xl border border-border bg-card p-4 overflow-y-auto space-y-4">
        {messages.length === 0 && (
          <div className="h-full flex flex-col items-center justify-center text-center text-muted-foreground gap-2">
            <Bot className="h-10 w-10 text-primary/60" />
            <p className="text-sm max-w-xs">Converse com o agente. Ele nunca inventa dados e não promete lucro garantido.</p>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`flex gap-3 ${m.role === "user" ? "justify-end" : ""}`}>
            {m.role === "assistant" && <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0"><Bot className="h-4 w-4 text-primary" /></div>}
            <div className={`max-w-[80%] rounded-xl px-4 py-2.5 text-sm whitespace-pre-wrap ${m.role === "user" ? "bg-primary text-primary-foreground" : "bg-secondary"}`}>
              {m.content || <span className="inline-block h-3 w-3 rounded-full bg-primary/50 animate-pulse" />}
            </div>
            {m.role === "user" && <div className="h-8 w-8 rounded-lg bg-secondary flex items-center justify-center shrink-0"><User className="h-4 w-4" /></div>}
          </div>
        ))}
        <div ref={endRef} />
      </div>

      <form onSubmit={send} className="flex gap-2 mt-4">
        <Input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Digite sua mensagem…" data-testid="chat-input" />
        <Button type="submit" disabled={streaming} data-testid="chat-send"><Send className="h-4 w-4" /></Button>
      </form>
    </div>
  );
}
